import { useState, useCallback, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import './Codepad.css';

const LANGUAGES = ['python', 'javascript', 'java', 'cpp'];

const HIGHLIGHT_COLORS = {
  warning: 'codepad-highlight-warning',
  error: 'codepad-highlight-error',
  info: 'codepad-highlight-info',
};

const SAMPLE_CASES = [
  { id: 1, input: 'nums = [2,7,11,15], target = 9', expected: '[0, 1]', status: null },
  { id: 2, input: 'nums = [3,2,4], target = 6', expected: '[1, 2]', status: null },
  { id: 3, input: 'nums = [3,3], target = 6', expected: '[0, 1]', status: null },
];

export default function Codepad({ language, initialCode, highlights, insertCode, agentTestCases, onClose }) {
  const [lang, setLang] = useState(language || 'python');
  const [code, setCode] = useState(initialCode || '# Start coding here...\n');
  const [isRunning, setIsRunning] = useState(false);
  const [outputOpen, setOutputOpen] = useState(false);
  const [activeCase, setActiveCase] = useState(0);
  const [cases, setCases] = useState(SAMPLE_CASES);
  const [terminalOutput, setTerminalOutput] = useState('');
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const decorationsRef = useRef([]);

  // Handle editor mount
  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Define highlight decoration styles
    monaco.editor.defineTheme('cultivate-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: { 'editor.background': '#111216' },
    });
    monaco.editor.setTheme('cultivate-dark');
  }, []);

  // Apply highlights from agent
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco || !highlights || highlights.length === 0) return;

    const decorations = highlights.map((h) => ({
      range: new monaco.Range(h.startLine, 1, h.endLine, 1),
      options: {
        isWholeLine: true,
        className: HIGHLIGHT_COLORS[h.color] || HIGHLIGHT_COLORS.warning,
        glyphMarginClassName: h.color === 'error' ? 'codepad-glyph-error' : 'codepad-glyph-warning',
      },
    }));

    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, decorations);
  }, [highlights]);

  // Handle dynamic test cases from agent
  useEffect(() => {
    if (!agentTestCases || !Array.isArray(agentTestCases)) return;
    const parsed = agentTestCases.map((tc, i) => ({
      id: i + 1,
      input: tc.input || '',
      expected: tc.expected || '',
      status: null,
    }));
    if (parsed.length > 0) setCases(parsed);
  }, [agentTestCases]);

  // Handle insert_code from agent
  useEffect(() => {
    if (!insertCode) return;
    setCode(insertCode.code);
    if (insertCode.language) setLang(insertCode.language);
    // Clear highlights when new code is inserted
    if (editorRef.current) {
      decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, []);
    }
  }, [insertCode]);

  const handleRun = useCallback(() => {
    setIsRunning(true);
    setOutputOpen(true);
    setTerminalOutput('Running...\n');

    setTimeout(() => {
      const updated = cases.map((c, i) => ({
        ...c,
        status: i === 2 ? 'fail' : 'pass',
      }));
      setCases(updated);
      setTerminalOutput(
        'Executed 3 test cases.\n\n' +
        '\u2713 Case 1: Passed  (nums=[2,7,11,15], target=9) \u2192 [0, 1]\n' +
        '\u2713 Case 2: Passed  (nums=[3,2,4], target=6) \u2192 [1, 2]\n' +
        '\u2717 Case 3: Failed  (nums=[3,3], target=6)\n' +
        '  Expected: [0, 1]\n' +
        '  Got:      [3, 3]\n\n' +
        'Runtime: 12ms'
      );
      setIsRunning(false);
    }, 1500);
  }, [cases]);

  const handleReset = useCallback(() => {
    setCode(initialCode || '# Start coding here...\n');
    setCases(SAMPLE_CASES);
    setTerminalOutput('');
    setOutputOpen(false);
    if (editorRef.current) {
      decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, []);
    }
  }, [initialCode]);

  return (
    <div className="codepad-layout">
      <div className="widget-card codepad-card">
        {/* Toolbar */}
        <div className="codepad-toolbar">
          <div className="codepad-toolbar-left">
            <span className="codepad-lang-dot" />
            <select
              className="codepad-lang-select"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="codepad-toolbar-right">
            <button className="codepad-icon-btn" onClick={handleReset} title="Reset">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 4v6h6M23 20v-6h-6" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
              </svg>
            </button>
            <button
              className={`codepad-run-btn ${isRunning ? 'running' : ''}`}
              onClick={handleRun}
              disabled={isRunning}
            >
              {isRunning ? 'Running...' : 'Run'}
              {!isRunning && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="codepad-editor">
          <Editor
            height="100%"
            language={lang}
            value={code}
            onChange={(v) => setCode(v || '')}
            theme="vs-dark"
            onMount={handleEditorMount}
            options={{
              fontSize: 13,
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              minimap: { enabled: false },
              padding: { top: 12 },
              scrollBeyondLastLine: false,
              lineNumbers: 'on',
              renderLineHighlight: 'line',
              cursorBlinking: 'smooth',
              automaticLayout: true,
              tabSize: 4,
              glyphMargin: true,
            }}
          />
        </div>

        {/* Output / Test Cases */}
        <div className={`codepad-output ${outputOpen ? 'open' : ''}`}>
          <button
            className="codepad-output-toggle"
            onClick={() => setOutputOpen(!outputOpen)}
          >
            <span>Output</span>
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2"
              style={{ transform: outputOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>

          {outputOpen && (
            <div className="codepad-output-body">
              <div className="codepad-cases-sidebar">
                {cases.map((c, i) => (
                  <button
                    key={c.id}
                    className={`codepad-case-tab ${activeCase === i ? 'active' : ''}`}
                    onClick={() => setActiveCase(i)}
                  >
                    {c.status === 'pass' && <span className="case-icon pass">&#10003;</span>}
                    {c.status === 'fail' && <span className="case-icon fail">&#10007;</span>}
                    {!c.status && <span className="case-icon idle">&#9679;</span>}
                    Case {c.id}
                  </button>
                ))}
              </div>
              <div className="codepad-case-detail">
                {cases[activeCase]?.status === 'fail' && (
                  <span className="case-fail-badge">Fail</span>
                )}
                {cases[activeCase] && (
                  <div className="case-io">
                    <div className="case-io-row">
                      <span className="case-io-label">Input</span>
                      <code className="case-io-value">{cases[activeCase].input}</code>
                    </div>
                    <div className="case-io-row">
                      <span className="case-io-label">Expected</span>
                      <code className="case-io-value">{cases[activeCase].expected}</code>
                    </div>
                  </div>
                )}
                {terminalOutput && (
                  <pre className="codepad-terminal">{terminalOutput}</pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
